import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription, takeUntil, distinctUntilChanged, filter, interval, switchMap, takeWhile } from 'rxjs';
import {
  AgentService,
  AgentAction,
  AgentChatResponse,
  AgentConversationMessage,
  BatchStatus,
} from '../../services/agent.service';
import { ShellChromeService } from '../../services/shell-chrome.service';
import { MdAiPanelService } from '../../services/md-ai-panel.service';
import { ContextService } from '../../services/context.service';
import { AiBatchTrackerService } from '../../services/ai-batch-tracker.service';
import { DEFAULT_UPLOAD_CAP, uploadCapMessage } from '../../utils/ai-upload-cap.util';
import { openChatRoute } from '../../utils/open-chat-route.util';
import { onlyNewBatchMessages } from '../../utils/ai-new-messages.util';
import { MarkdownPipe } from '../../pipes/markdown/markdown.pipe';
import { AiBatchProgressComponent } from '../ai/batch-progress/batch-progress.component';

const SESSION_KEY_PREFIX = 'mdai_session_';
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

@Component({
  selector: 'app-md-ai-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, MarkdownPipe, AiBatchProgressComponent],
  templateUrl: './md-ai-panel.component.html',
  styleUrls: ['./md-ai-panel.component.scss'],
})
export class MdAiPanelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Panel state
  isPanelOpen = false;
  isFullscreen = false;

  // Chat
  chatMessage = '';
  chatHistory: AgentConversationMessage[] = [];
  currentSessionId: string | null = null;
  chatLoading = false;

  // Voice
  isRecording = false;
  speechSupported = false;
  private recognition: any = null;

  // Bulk AP batch (upload → background fan-out → per-invoice step chips via polling)
  /** Current batch, or null. Replaced wholesale on each poll, never appended to. */
  /**
   * Every batch this user has running, from the shared tracker.
   *
   * <p>A list rather than one, because a user may have two going at once — and because the
   * tracker is what owns them now. The panel used to own the poll, which meant closing it lost
   * the progress of work that was still running on the server.
   */
  activeBatches: BatchStatus[] = [];

  /** Overwritten from `/agent/status` on init. See `ai-upload-cap.util`. */
  uploadCap = DEFAULT_UPLOAD_CAP;



  // User
  userName = '';
  private currentUserId: string | null = null;
  private currentRoleId: string | null = null;

  // Attachments — files are staged (temporary), not permanent. We hold the
  // opaque `stagingId` (sent with the chat message) and a short-lived
  // `previewUrl` SAS for thumbnails. Promotion to permanent storage happens
  // server-side only when the resulting proposal is confirmed.
  pendingAttachments: {
    file: File;
    name: string;
    stagingId?: string;
    previewUrl?: string;
    uploading: boolean;
    error?: boolean;
  }[] = [];
  attachmentError: string | null = null;

  // Action progress items (shown during batch creation)
  actionProgressItems: { label: string; status: 'pending' | 'in_progress' | 'done' | 'error' }[] = [];

  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('chatInput') chatInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(
    private agentService: AgentService,
    private panelService: MdAiPanelService,
    private contextService: ContextService,
    private shellChrome: ShellChromeService,
    private tracker: AiBatchTrackerService,
    private router: Router,
  ) {}

  /**
   * A rendered chat message can contain an in-app link (e.g. "Open draft") from
   * `MarkdownPipe` — `data-route="/accounts-payable/details/<id>"`. One delegated
   * listener on the message list decides where it goes: a new tab on a desktop, so
   * the conversation stays put; the edit form in place on a phone. See `openChatRoute`.
   */
  onMessagesClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement).closest('a[data-route]');
    if (!anchor) return;
    event.preventDefault();
    const route = anchor.getAttribute('data-route');
    if (route) openChatRoute(this.router, route);
  }

  /** Stable identity for the chip lists, so a poll does not re-create every row. */
  trackBatch(_index: number, batch: BatchStatus): string {
    return batch.batchId;
  }

  /** "Creating drafts…" across every running batch, so two uploads read as one total. */
  get batchProgressLabel(): string {
    const remaining = this.activeBatches.reduce(
      (n, b) => n + b.summary.queued + b.summary.creating, 0,
    );

    return remaining === 1
      ? 'Creating 1 draft…'
      : `Creating ${remaining} drafts…`;
  }

  private getSessionKey(): string {
    return `${SESSION_KEY_PREFIX}${this.currentUserId || 'anon'}_${this.currentRoleId || 'norole'}`;
  }

  ngOnInit(): void {
    this.followBatches();

    // The agent enforces the real cap and can change it from an environment variable, so it is
    // read rather than hardcoded — a stale number here means the control accepts files the
    // agent then refuses, and the product says two different things.
    this.agentService
      .getStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          if (status.limits?.maxInvoicesPerBatch) this.uploadCap = status.limits.maxInvoicesPerBatch;
        },
        error: () => {},
      });

    // Detect Web Speech API
    const win = window as any;
    this.speechSupported = !!(win.SpeechRecognition || win.webkitSpeechRecognition);

    // Subscribe to panel state
    this.panelService.panelOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(open => {
        this.isPanelOpen = open;

        // The rail open/closed states are two different shell layouts: 280 + 1160 closed,
        // 81 + 900 + 459 open. Asking for the collapse here rather than in the header button
        // means it also holds when the rail is opened any other way.
        // The tracker keeps watching either way — the batch does not stop because the panel
        // closed, and the toasts are what tell someone on another page. Being closed just makes
        // it ask less often.
        this.tracker.setVisible(open);

        if (open) {
          this.shellChrome.requestCollapsedNav('assist-rail');
          this.requestScrollToBottom();
          this.focusChatInput();
        } else {
          this.shellChrome.releaseCollapsedNav('assist-rail');
        }
      });

    this.panelService.fullscreen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(fs => (this.isFullscreen = fs));

    // Subscribe to user changes
    this.contextService.user
      .pipe(
        takeUntil(this.destroy$),
        filter(u => !!u),
        distinctUntilChanged((a, b) => a?.id === b?.id),
      )
      .subscribe(user => {
        const newUserId = user?.id || null;
        if (this.currentUserId && newUserId !== this.currentUserId) {
          this.resetChatState();
        }
        this.currentUserId = newUserId;
        this.userName = user?.firstName || 'there';
        this.currentSessionId = localStorage.getItem(this.getSessionKey());
        if (this.currentSessionId) {
          this.loadConversationHistory();
          // Re-attach to anything this session left running, so a reload picks the chips back up
          // rather than leaving work in progress invisible.
          this.tracker.restore(this.currentSessionId);
        }
      });

    // Reset on role change
    this.contextService.selectedRoleId$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(roleId => {
        if (this.currentRoleId && roleId !== this.currentRoleId) {
          localStorage.removeItem(this.getSessionKey());
          this.resetChatState();
        }
        this.currentRoleId = roleId;
        this.currentSessionId = localStorage.getItem(this.getSessionKey());
        if (this.currentSessionId && this.chatHistory.length === 0) {
          this.loadConversationHistory();
        }
      });

    // Subscribe to loading states
    this.agentService.chatLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => (this.chatLoading = loading));
  }

  ngOnDestroy(): void {
    // A component destroyed while the rail is open would otherwise leave the nav collapsed with
    // nothing holding it.
    this.shellChrome.releaseCollapsedNav('assist-rail');
    this.stopRecording();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Panel actions ---

  closePanel(): void {
    this.panelService.closePanel();
  }

  toggleFullscreen(): void {
    this.panelService.toggleFullscreen();
  }

  // --- Chat ---

  sendMessage(): void {
    const text = this.chatMessage.trim();
    if (!text && this.pendingAttachments.length === 0) return;
    // Surface why we're not sending instead of silently doing nothing.
    if (this.pendingAttachments.some(a => a.uploading)) {
      this.attachmentError = 'Please wait — your file is still uploading.';
      return;
    }
    if (this.pendingAttachments.some(a => a.error)) {
      this.attachmentError = 'A file failed to upload. Remove it (or tap retry) before sending.';
      return;
    }
    this.attachmentError = null;

    // Only staged files (those with a stagingId) are ready to send.
    const readyAttachments = this.pendingAttachments.filter(a => a.stagingId);

    // Files were attached but none staged (no stagingId) — never send an empty
    // chat (the agent 400s with "Missing required: message or attachments").
    if (this.pendingAttachments.length > 0 && readyAttachments.length === 0) {
      this.attachmentError = 'Your invoice isn’t attached yet (no staging id). Please remove it and upload again.';
      return;
    }

    // Payload for the agent: opaque stagingId + metadata it echoes back at
    // promote-on-confirm time, plus the short-lived preview SAS the agent needs
    // to DOWNLOAD the staged invoice for OCR (without it OCR fails with
    // "Could not download attachment"). No permanent URL exists yet.
    const requestAttachments = readyAttachments.map(a => ({
      stagingId: a.stagingId!,
      name: a.name,
      contentType: a.file.type,
      size: a.file.size,
      previewUrl: a.previewUrl || undefined,
    }));

    // Local bubble display uses the short-lived preview SAS for thumbnails.
    const displayAttachments = readyAttachments.map(a => ({
      name: a.name,
      url: a.previewUrl || '',
      contentType: a.file.type,
      size: a.file.size,
    }));

    this.chatHistory.push({
      role: 'user',
      content: text,
      attachments: displayAttachments.length > 0 ? displayAttachments : undefined,
    });
    this.chatMessage = '';
    this.requestScrollToBottom();

    const request: any = {
      message: text,
      sessionId: this.currentSessionId || undefined,
    };

    if (requestAttachments.length > 0) {
      request.attachments = requestAttachments;
      this.pendingAttachments = [];
    }

    this.agentService
      .chat(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AgentChatResponse) => {
          if (response.sessionId) {
            this.currentSessionId = response.sessionId;
            localStorage.setItem(this.getSessionKey(), response.sessionId);
          }

          this.chatHistory.push({ role: 'assistant', content: response.reply });
          this.requestScrollToBottom();
          this.focusChatInput();

          // Bulk invoice processing runs in the background — poll the batch for per-invoice
          // progress. No cards: the drafts are reviewed in the AP module.
          if (response.status === 'processing' && response.batchId && this.currentSessionId) {
            this.tracker.track(this.currentSessionId, response.batchId);
            return;
          }

          this.requestScrollToBottom();
        },
        // Last-resort guard: AgentService.chat already maps errors to a reply,
        // but never leave the user staring at no response if something slips through.
        error: () => {
          this.chatHistory.push({
            role: 'assistant',
            content: 'Something went wrong reaching the assistant. Please try again.',
          });
          this.requestScrollToBottom();
          this.focusChatInput();
        },
      });
  }

  /**
   * Follow the shared tracker, and pull in what the agent wrote while we were not looking.
   *
   * <p>The panel no longer polls. It used to, and the poll died with the component — so closing
   * the panel lost the progress of work that was still running on the server. The tracker is a
   * root service that outlives both this and the AI-First page, and polls once for both.
   *
   * <p>The transcript lines come from the agent rather than being composed here: a batch writes
   * what it did into the conversation as it goes, which is what makes the outcome survive a
   * closed tab. Fetching only what is new avoids re-reading the whole conversation every time an
   * invoice lands.
   */
  private followBatches(): void {
    this.tracker.batches$
      .pipe(takeUntil(this.destroy$))
      .subscribe((batches: BatchStatus[]) => { this.activeBatches = batches; });

    this.tracker.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.appendNewMessages());
  }

  /**
   * Append whatever the agent has written since the last message we hold.
   *
   * <p>Only the batch kinds. A local reply has no id — it was pushed straight into the array
   * when the response arrived — so filtering by kind is also what stops it being appended twice.
   */
  private appendNewMessages(): void {
    if (!this.currentSessionId) return;

    const lastId = this.chatHistory.reduce((max, m) => Math.max(max, m.id ?? 0), 0);

    this.agentService
      .getConversation(this.currentSessionId, lastId || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          // Deduped by id: the tracker fires an `item` and a `batch` event in the same poll when
          // the last invoice finishes, so two fetches share one `lastId` and return the same
          // lines. Seen on dev (2026-09-30) as every batch line rendered twice.
          const fresh = onlyNewBatchMessages(result.messages ?? [], this.chatHistory);

          if (fresh.length === 0) return;

          this.chatHistory.push(...fresh);
          this.requestScrollToBottom();
        },
        // A failed fetch costs this update and nothing else: the next event tries again, and
        // the messages are on the server either way.
        error: () => {},
      });
  }

  /**
   * Stop a running batch. In-flight invoices finish; nothing new is claimed.
   *
   * Drafts already created are left alone — the agent has no delete capability, so an unwanted
   * draft is deleted in the AP module by someone with permission to do it. That is a deliberate
   * change from the OCR cancel this replaces, which discarded unconfirmed cards and their
   * auto-created drafts.
   */
  onCancelBatch(batchId: string): void {
    this.tracker.cancel(batchId);
  }

  private loadConversationHistory(): void {
    if (!this.currentSessionId) return;
    this.agentService
      .getConversation(this.currentSessionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (result.messages && result.messages.length > 0) {
            this.chatHistory = result.messages;
            this.requestScrollToBottom();
          }
        },
        error: () => {
          localStorage.removeItem(this.getSessionKey());
          this.currentSessionId = null;
        },
      });
  }

  newChat(): void {
    this.resetChatState();
    localStorage.removeItem(this.getSessionKey());
  }

  private resetChatState(): void {
    this.chatHistory = [];
    this.currentSessionId = null;
    this.pendingAttachments = [];
    this.attachmentError = null;
    this.chatMessage = '';
    this.actionProgressItems = [];
    this.activeBatches = [];
    // The tracker is shared, so a new chat has to tell it to forget — otherwise the previous
    // conversation's batches keep raising toasts into a session they do not belong to.
    this.tracker.reset();
  }


  // --- Copy message ---

  copyMessage(content: string): void {
    navigator.clipboard.writeText(content).catch(() => {});
  }

  // --- Voice ---

  toggleVoiceInput(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  private startRecording(): void {
    const win = window as any;
    const SpeechRecognition = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'en-US';
    this.recognition.interimResults = true;
    this.recognition.continuous = false;

    let finalTranscript = '';
    this.recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      this.chatMessage = (finalTranscript + interim).trim();
    };
    this.recognition.onend = () => (this.isRecording = false);
    this.recognition.onerror = () => (this.isRecording = false);
    this.recognition.start();
    this.isRecording = true;
  }

  private stopRecording(): void {
    if (this.recognition) {
      this.recognition.stop();
      this.recognition = null;
    }
    this.isRecording = false;
  }

  // --- Attachments ---

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    this.attachmentError = null;

    const attempted = this.pendingAttachments.length + input.files.length;
    if (attempted > this.uploadCap) {
      this.attachmentError = uploadCapMessage(this.uploadCap, attempted);
      input.value = '';
      return;
    }

    const oversized: string[] = [];
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversized.push(file.name);
        continue;
      }
      this.addAttachment(file);
    }
    if (oversized.length > 0) {
      this.attachmentError = `${oversized.length === 1 ? 'File' : 'Files'} exceed 50 MB: ${oversized.join(', ')}`;
    }
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.pendingAttachments.splice(index, 1);
  }

  retryAttachment(index: number): void {
    const att = this.pendingAttachments[index];
    if (!att || !att.error) return;
    att.error = false;
    att.uploading = true;
    this.uploadFile(att);
  }

  private addAttachment(file: File): void {
    const attachment: typeof this.pendingAttachments[0] = {
      file, name: file.name, uploading: true, stagingId: undefined, previewUrl: undefined, error: false,
    };
    this.pendingAttachments.push(attachment);
    this.uploadFile(attachment);
  }

  private uploadFile(attachment: typeof this.pendingAttachments[0]): void {
    // Upload to the AP staging endpoint, which returns a `stagingId` the agent
    // needs to process the invoice. (The default/legacy endpoints return a
    // FileModel with no stagingId, which would silently drop the attachment.)
    this.agentService.uploadAttachment(attachment.file, 'AP')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (!result?.stagingId) {
            // No staging id → the file can't be sent to the agent. Surface it
            // rather than letting sendMessage drop the attachment and post an
            // empty chat (which the agent rejects with 400).
            attachment.uploading = false;
            attachment.error = true;
            this.attachmentError = `Couldn't attach "${attachment.name}" (upload did not return a staging id).`;
            return;
          }
          attachment.stagingId = result.stagingId;
          attachment.previewUrl = result.previewUrl;
          // Cleared LAST, matching the AI-First shell. `data-state` on the upload item is derived
          // from this flag, so clearing it before stagingId lands describes an attachment as
          // ready while it still has nothing to send. Change detection happens to close that
          // window today — both assignments land in one cycle — which is precisely why the
          // ordering is worth fixing rather than relying on.
          attachment.uploading = false;
        },
        error: () => {
          attachment.uploading = false;
          attachment.error = true;
          this.attachmentError = `Failed to upload "${attachment.name}". Tap to retry.`;
        },
      });
  }

  // --- Input handling ---

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  autoGrowTextarea(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }

  // --- Helpers ---


  getFileSizeLabel(size: number): string {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
    return (size / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        const el = this.chatContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    } catch (_) {}
  }

  private requestScrollToBottom(): void {
    setTimeout(() => this.scrollToBottom(), 0);
    setTimeout(() => this.scrollToBottom(), 150);
  }

  private focusChatInput(): void {
    setTimeout(() => {
      if (this.chatInput?.nativeElement && !this.chatLoading) {
        this.chatInput.nativeElement.focus();
      }
    }, 100);
  }
}
